import React, { useState, useEffect } from "react";
import {
  Clock,
  User,
  FileText,
  Search,
  Calendar,
  Filter,
  Download,
} from "lucide-react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ActivityLogs = ({ moduleName = "System", activityTypes = null }) => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  // Generate module-specific mock data
  const generateMockLogs = () => {
    const baseActions = activityTypes || [
      "Record Created", "Record Updated", "Record Deleted", 
      "Data Exported", "Settings Changed", "User Access"
    ];
    
    return [
      {
        id: 1,
        employeeId: "EMP-2024-001",
        action: baseActions[0],
        modifiedBy: "HR001",
        date: "2024-01-15T10:30:00Z",
        details: `${moduleName} record created with basic information`
      },
      {
        id: 2,
        employeeId: "EMP-2024-002",
        action: baseActions[1],
        modifiedBy: "HR002",
        date: "2024-01-15T14:45:00Z",
        details: `${moduleName} record updated successfully`
      },
      {
        id: 3,
        employeeId: "EMP-2024-001",
        action: baseActions[3],
        modifiedBy: "MGR001",
        date: "2024-01-14T16:20:00Z",
        details: `${moduleName} data exported to Excel`
      },
      {
        id: 4,
        employeeId: "EMP-2024-003",
        action: baseActions[4],
        modifiedBy: "ADMIN001",
        date: "2024-01-14T09:15:00Z",
        details: `${moduleName} configuration updated`
      },
      {
        id: 5,
        employeeId: "EMP-2024-002",
        action: baseActions[5],
        modifiedBy: "SYSTEM",
        date: "2024-01-13T09:00:00Z",
        details: `User accessed ${moduleName} module`
      }
    ];
  };

  useEffect(() => {
    setTimeout(() => {
      const mockLogs = generateMockLogs();
      setLogs(mockLogs);
      setFilteredLogs(mockLogs);
      setLoading(false);
    }, 1000);
  }, [moduleName, activityTypes]);

  useEffect(() => {
    let filtered = logs;

    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.modifiedBy.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (dateFilter) {
      filtered = filtered.filter(log =>
        log.date.startsWith(dateFilter)
      );
    }

    if (actionFilter !== "All") {
      filtered = filtered.filter(log =>
        log.action === actionFilter
      );
    }

    setFilteredLogs(filtered);
  }, [searchTerm, dateFilter, actionFilter, logs]);

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getActionColor = (action) => {
    if (action.includes("Created") || action.includes("Approved")) return "bg-green-100 text-green-800";
    if (action.includes("Updated") || action.includes("Modified")) return "bg-blue-100 text-blue-800";
    if (action.includes("Deleted") || action.includes("Rejected")) return "bg-red-100 text-red-800";
    if (action.includes("Uploaded") || action.includes("Downloaded")) return "bg-purple-100 text-purple-800";
    return "bg-gray-100 text-gray-800";
  };

  const exportToExcel = () => {
    const exportData = filteredLogs.map(log => ({
      'Employee ID': log.employeeId,
      'Action': log.action,
      'Modified By': log.modifiedBy,
      'Date': formatDateTime(log.date),
      'Details': log.details
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Activity Logs");
    XLSX.writeFile(wb, `${moduleName}_Activity_Logs_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Set fonts
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`${moduleName} - Activity Logs`, 20, 20);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 20, 30);
    doc.text(`Total Records: ${filteredLogs.length}`, 20, 40);

    const tableData = filteredLogs.map(log => [
      log.employeeId,
      log.action,
      log.modifiedBy,
      formatDateTime(log.date),
      log.details
    ]);

    autoTable(doc, {
      head: [['Employee ID', 'Action', 'Modified By', 'Date', 'Details']],
      body: tableData,
      startY: 50,
      styles: { 
        fontSize: 8,
        font: 'helvetica'
      },
      headStyles: {
        fillColor: [79, 70, 229],
        textColor: 255,
        fontStyle: 'bold'
      }
    });

    doc.save(`${moduleName}_Activity_Logs_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const uniqueActions = [...new Set(logs.map(log => log.action))];

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border font-roboto">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900 font-poppins">
              Activity Logs - {moduleName}
            </h3>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={exportToExcel}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Download className="h-4 w-4 mr-2" />
              Excel
            </button>
            <button
              onClick={exportToPDF}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="relative">
            <Calendar className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="relative">
            <Filter className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="All">All Actions</option>
              {uniqueActions.map(action => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center text-sm text-gray-500">
            Showing {filteredLogs.length} of {logs.length} logs
          </div>
        </div>
      </div>

      {/* Activity Logs Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">
                Employee ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">
                Modified By
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                  <Clock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p>No activity logs found matching your criteria.</p>
                </td>
              </tr>
            ) : (
              filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {log.employeeId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.modifiedBy}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(log.date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">
                    {log.details}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActivityLogs;